import React, { useEffect, useRef, useState } from "react";

// Small built-in emoji picker (no external library). The last emojis used are
// remembered in this browser.
const CATEGORIES = [
  {
    key: "smileys",
    icon: "😀",
    emojis:
      "😀 😃 😄 😁 😆 😅 😂 🤣 😊 😇 🙂 😉 😌 😍 🥰 😘 😗 😙 😚 😋 😛 😜 🤪 😝 🤗 🤭 🤫 🤔 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 😮‍💨 🤥 😔 😪 😴 😷 🤒 🤕 🥵 🥶 😵 🤯 🥳 😎 🤓 🧐 😕 😟 🙁 😮 😯 😲 😳 🥺 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠".split(" "),
  },
  {
    key: "gestures",
    icon: "👍",
    emojis:
      "👍 👎 👌 ✌️ 🤞 🤟 🤘 🤙 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐️ 🖖 👋 👏 🙌 👐 🤲 🤝 🙏 ✍️ 💪 🫶 👀 🧠".split(" "),
  },
  {
    key: "hearts",
    icon: "❤️",
    emojis:
      "❤️ 🧡 💛 💚 💙 💜 🤎 🖤 🤍 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 ✨ ⭐ 🌟 💯 ✅ ❌ ❗ ❓ 🔥 🎉 🎊".split(" "),
  },
  {
    key: "school",
    icon: "📚",
    emojis:
      "📚 📖 📝 ✏️ 🖊️ 📒 📓 📕 📗 📘 📙 📐 📏 🧮 🎒 🏫 🧑‍🏫 👩‍🏫 👨‍🏫 🧑‍🎓 🎓 🏆 🥇 🏅 🎨 🎵 🔬 🧪 💻 📅 ⏰ 🕌 🕋 📿".split(" "),
  },
  {
    key: "nature",
    icon: "🌸",
    emojis:
      "🐶 🐱 🐭 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐸 🐵 🐔 🐧 🐦 🦋 🐝 🌸 🌼 🌻 🌹 🌷 🌳 🌴 🍀 🌙 ☀️ 🌈 ☁️ ❄️ 🌊".split(" "),
  },
  {
    key: "food",
    icon: "🍎",
    emojis:
      "🍎 🍊 🍋 🍌 🍉 🍇 🍓 🍒 🍑 🥝 🍅 🥕 🌽 🥐 🍞 🧀 🥚 🍕 🍔 🍟 🌮 🍝 🍰 🎂 🍪 🍩 🍫 🍬 🥛 ☕ 🍵 🧃".split(" "),
  },
];

const RECENT_KEY = "casaclass:recentEmojis";

function loadRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(list) ? list.slice(0, 24) : [];
  } catch {
    return [];
  }
}

function saveRecent(emoji) {
  try {
    const list = [emoji, ...loadRecent().filter((e) => e !== emoji)].slice(0, 24);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // storage unavailable
  }
}

export default function EmojiPicker({ onPick, onClose, labels = {} }) {
  const [recent] = useState(loadRecent);
  const [category, setCategory] = useState(recent.length ? "recent" : "smileys");
  const ref = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose?.();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const tabs = [
    ...(recent.length ? [{ key: "recent", icon: "🕘", emojis: recent }] : []),
    ...CATEGORIES,
  ];
  const current = tabs.find((t) => t.key === category) || tabs[0];

  return (
    <div className="chat-emoji" ref={ref} role="dialog" aria-label={labels.title || "Emoji"}>
      <div className="chat-emoji__tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === current.key}
            title={labels[tab.key] || tab.key}
            className={tab.key === current.key ? "is-active" : ""}
            onClick={() => setCategory(tab.key)}
          >
            {tab.icon}
          </button>
        ))}
      </div>
      <div className="chat-emoji__grid">
        {current.emojis.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="chat-emoji__item"
            onClick={() => {
              saveRecent(emoji);
              onPick(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
